

import React, { useContext } from "react";
import { GlobalContext } from "../../../../Context/GlobalContext";
import { toast, ToastContainer } from "react-toastify";
import {
  User,
  ShieldCheck,
  ArrowRight,
  Loader2,
  CheckCircle2,
  BadgeCheck,
  UserRoundCheck,
  LockKeyhole,
  ClipboardCheck,
  UserPlus,
} from "lucide-react";

import front from "../../../../assets/rightbanner.webp";
import delhi from "../../../../assets/indiagate.webp";
import noida from "../../../../assets/noida.webp";
import ghaziabad from "../../../../assets/ghaziabad.webp";
import gurugram from "../../../../assets/gurugram.webp";
import useCustomerDetails from "../../../Services/Customer/useCustomerDetails";

const AddCustomerDetails = ({
  formData,
  setFormData,
  onNext,
  selectedCity,
}) => {
  const { customerDetailsLoading, addCustomerDetailsApi } =
    useCustomerDetails();

  const { userCode } = useContext(GlobalContext);

  const normalizeCityName = (city = "") => {
    const value = String(city).trim().toLowerCase();

    if (value.includes("delhi")) return "Delhi";
    if (value.includes("noida")) return "Noida";
    if (value.includes("gurugram") || value.includes("gurgaon"))
      return "Gurugram";
    if (value.includes("gaziabad") || value.includes("ghaziabad"))
      return "Ghaziabad";
    return "Delhi";
  };

  const finalSelectedCity = normalizeCityName(
    selectedCity ||
      formData?.city ||
      sessionStorage.getItem("selectedCity") ||
      "Delhi",
  );

  const cityImages = {
    Delhi: delhi,
    Noida: noida,
    Ghaziabad: ghaziabad,
    Gaziabad: ghaziabad,
    Gurugram: gurugram,
    Gurgaon: gurugram,
  };

  const rightImage = cityImages[finalSelectedCity] || front;

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      toast.error("Please enter first name");
      return false;
    }

    if (!formData.lastName.trim()) {
      toast.error("Please enter last name");
      return false;
    }

    if (!formData.privacyPolicy) {
      toast.error("Please accept privacy policy");
      return false;
    }

    return true;
  };

  const handleSubmitCustomerDetails = async () => {
    if (!validateForm()) return;

    if (!userCode) {
      toast.error("User code missing. Please login again.");
      return;
    }

    const result = await addCustomerDetailsApi({
      userCode,
      firstName: formData.firstName,
      lastName: formData.lastName,
      privacyPolicy: formData.privacyPolicy ? "Y" : "N",
    });

    if (result?.responseCode === "00") {
      toast.success(
        result?.responseMessage || "Customer details saved successfully",
      );

      if (onNext) {
        onNext();
      }
    } else {
      toast.error(result?.responseMessage || "Unable to save details");
    }
  };

  return (
    <>
      <ToastContainer />

      <div className="relative z-10 mx-auto mt-12 grid w-full max-w-[1020px] overflow-hidden rounded-[26px] border border-white/80 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.16)] sm:mt-6 md:mt-12 lg:mt-[170px] lg:h-[790px] lg:grid-cols-[57%_43%]">
        {/* LEFT FORM */}
        <div className="flex h-full min-h-0 flex-col bg-white p-5 sm:p-6 lg:px-8 lg:py-6">
          {/* TOP HEADER */}
          <div className="shrink-0">
            <div className="mb-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                  <UserRoundCheck size={25} />
                </div>

                <div>
                  <h4 className="text-[15px] font-bold text-slate-900">
                    Packky Profile
                  </h4>
                  <p className="text-[12px] text-slate-500">One-time setup</p>
                </div>
              </div>

              <div className="hidden rounded-full bg-blue-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-blue-700 sm:inline-flex">
                Secure Details
              </div>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-blue-700">
              <ShieldCheck size={13} />
              Customer Verification
            </div>
          </div>

          <div className="flex flex-1 flex-col justify-start pt-10">
            {/* TITLE */}
            <div className="mb-6">
              <h2 className="text-[30px] font-bold tracking-tight text-slate-900">
                Complete Your Profile
              </h2>

              <p className="mt-2 max-w-[430px] text-[15px] leading-6 text-slate-500">
                Please add your basic details once so we can make your future
                bookings faster and smoother.
              </p>
            </div>

            {/* FORM */}
            <div className="space-y-4">
              {/* FIRST NAME */}
              <div>
                <label className="mb-2 block text-[14px] font-semibold text-slate-600">
                  First Name
                </label>

                <div className="group flex h-[50px] items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 transition focus-within:border-blue-500 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-100">
                  <User size={17} className="shrink-0 text-slate-400" />

                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Enter first name"
                    className="w-full bg-transparent text-[16px] font-medium text-slate-700 outline-none placeholder:text-slate-400"
                  />
                </div>
              </div>

              {/* LAST NAME */}
              <div>
                <label className="mb-2 block text-[14px] font-semibold text-slate-600">
                  Last Name
                </label>

                <div className="group flex h-[50px] items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 transition focus-within:border-blue-500 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-100">
                  <User size={17} className="shrink-0 text-slate-400" />

                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Enter last name"
                    className="w-full bg-transparent text-[16px] font-medium text-slate-700 outline-none placeholder:text-slate-400"
                  />
                </div>
              </div>

              {/* PRIVACY POLICY */}
              <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3.5">
                <div className="flex items-start gap-3">
                  <input
                    id="privacyPolicy"
                    type="checkbox"
                    name="privacyPolicy"
                    checked={formData.privacyPolicy}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-blue-600"
                  />

                  <label
                    htmlFor="privacyPolicy"
                    className="cursor-pointer text-[13px] leading-6 text-slate-600"
                  >
                    I agree to the{" "}
                    <a
                      href="/privacy-policy"
                      className="font-bold text-blue-700 underline"
                    >
                      Privacy Policy
                    </a>{" "}
                    and allow Packky to save my details for faster booking.
                  </label>
                </div>
              </div>

              {/* BUTTON */}
              <button
                type="button"
                onClick={handleSubmitCustomerDetails}
                disabled={customerDetailsLoading}
                className="group flex h-[49px] w-full items-center justify-center gap-2 rounded-2xl bg-[#1f2d3f] px-5 text-[15px] font-bold text-white shadow-lg shadow-slate-300 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#172334] hover:shadow-xl active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {customerDetailsLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Please wait...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight
                      size={18}
                      className="transition group-hover:translate-x-0.5"
                    />
                  </>
                )}
              </button>

              {/* BENEFIT CARDS */}
              <div className="grid grid-cols-3 gap-3 pt-2">
                <div className="rounded-2xl border border-blue-100 bg-blue-50/80 p-4">
                  <div className="mb-2.5 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
                    <BadgeCheck size={18} />
                  </div>

                  <h4 className="text-[14px] font-bold text-slate-800">
                    Verified User
                  </h4>

                  <p className="mt-1 text-[12px] leading-5 text-slate-500">
                    Keep your account details clean.
                  </p>
                </div>

                <div className="rounded-2xl border border-blue-100 bg-blue-50/80 p-4">
                  <div className="mb-2.5 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
                    <ClipboardCheck size={18} />
                  </div>

                  <h4 className="text-[14px] font-bold text-slate-800">
                    Quick Booking
                  </h4>

                  <p className="mt-1 text-[12px] leading-5 text-slate-500">
                    Skip this step next time.
                  </p>
                </div>

                <div className="rounded-2xl border border-blue-100 bg-blue-50/80 p-4">
                  <div className="mb-2.5 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
                    <LockKeyhole size={18} />
                  </div>

                  <h4 className="text-[14px] font-bold text-slate-800">
                    Safe Data
                  </h4>

                  <p className="mt-1 text-[12px] leading-5 text-slate-500">
                    Your details stay protected.
                  </p>
                </div>
              </div>

              {/* INFO STRIP */}
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="mb-2 flex items-center gap-2">
                  <CheckCircle2 size={17} className="text-green-600" />

                  <h4 className="text-[15px] font-bold text-slate-800">
                    Why we need this?
                  </h4>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] text-slate-600">
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                    Better delivery updates
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                    Faster repeat bookings
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                    Safer customer profile
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                    Smooth order flow
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT DESIGN */}
        <div className="relative hidden h-full w-full items-center justify-center overflow-hidden bg-gradient-to-br from-[#eaf7ff] via-[#b9e5ff] to-[#188bdd] lg:flex">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/30 blur-3xl" />
          <div className="absolute -bottom-24 -left-20 h-80 w-80 rounded-full bg-blue-300/40 blur-3xl" />

          {/* BIG LETTER BACKGROUND */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="select-none text-[300px] font-black leading-none text-white/20 xl:text-[360px]">
              P
            </span>
          </div>

          {/* TOP FLOATING CARD */}
          <div className="absolute left-6 top-6 z-20 rounded-2xl border border-white/40 bg-white/35 px-4 py-3 shadow-lg backdrop-blur-md">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-blue-500">
                <UserPlus size={20} />
              </div>

              <div>
                <p className="text-[12px] font-medium text-blue-950/70">
                  Profile Setup
                </p>
                <h5 className="text-sm font-bold text-blue-950">
                  One-time Details
                </h5>
              </div>
            </div>
          </div>

          {/* BOTTOM FLOATING CARD */}
          <div className="absolute bottom-5 left-6 right-6 z-20 rounded-3xl border border-white/50 bg-white/35 p-4 shadow-xl backdrop-blur-md">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-blue-500">
                <ShieldCheck size={21} />
              </div>

              <div>
                <h4 className="text-sm font-bold text-blue-950">
                  Save time on every booking
                </h4>
                <p className="text-[12px] text-blue-950/70">
                  Your profile helps us pre-fill your next order flow.
                </p>
              </div>
            </div>
          </div>

          {/* IMAGE */}
          <div className="relative z-10 flex h-full w-full items-center justify-center px-5">
            <img
              src={front}
              alt="delivery"
              className="max-h-[520px] w-full max-w-[480px] object-contain drop-shadow-[0_30px_35px_rgba(15,23,42,0.25)]"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AddCustomerDetails;

